import type { CanvasElement } from '../types';
import type { EnhancedFieldMapping } from '../types/mapping';

/**
 * SINGLE SOURCE OF TRUTH for "is this element dynamic?".
 *
 * An element is dynamic when BOTH:
 *   1. Its `contentType` is `'DYNAMIC'` (user-declared intent), and
 *   2. It has at least one persisted field mapping OR a legacy inline
 *      `dynamicField` that has not yet been migrated.
 *
 * Previously PropertyPanel and LayerPanel computed this independently and
 * drifted apart for legacy projects whose inline `dynamicField` had been
 * migrated into `cs_element_mappings` but whose `contentType` had somehow
 * desynced. Always route through this helper.
 */
export function elementHasMapping(
  element: CanvasElement,
  mappings: EnhancedFieldMapping[] | undefined
): boolean {
  if (mappings && mappings.length > 0) return true;
  const inline = (element as unknown as { dynamicField?: unknown }).dynamicField;
  return !!inline;
}

export function isElementDynamic(
  element: CanvasElement,
  mappings: EnhancedFieldMapping[] | undefined
): boolean {
  return element.contentType === 'DYNAMIC' && elementHasMapping(element, mappings);
}

export function getCompatibleMappableProperties(
  elementTypes: Array<'text' | 'image' | 'shape' | 'button' | 'path' | 'svg' | 'line' | 'group'>
): string[] {
  if (elementTypes.length === 0) return [];

  const TYPE_PROPERTY_MATRIX: Record<string, string[]> = {
    text:   ['textContent', 'fontColor', 'fontFamily', 'fontSize', 'fontWeight', 'textTransform', 'opacity'],
    image:  ['imageUrl', 'imageSrc', 'opacity'],
    shape:  ['fillColor', 'strokeColor', 'opacity'],
    button: ['textContent', 'buttonLabel', 'buttonHref', 'fontColor', 'buttonFillColor', 'opacity'],
    path:   ['fillColor', 'strokeColor', 'opacity'],
    svg:    ['fillColor', 'strokeColor', 'opacity'],
    line:   ['strokeColor', 'opacity'],
    group:  [],
  };

  const uniqueTypes = Array.from(new Set(elementTypes));
  if (uniqueTypes.some(t => !TYPE_PROPERTY_MATRIX[t])) return [];

  const propertySets = uniqueTypes.map(t => new Set(TYPE_PROPERTY_MATRIX[t]));
  if (propertySets.length === 0) return [];

  const intersection = Array.from(propertySets[0]).filter(prop =>
    propertySets.every(set => set.has(prop))
  );

  return intersection.sort();
}
